export * from './money.ts';
export * from './paymentAccounting.ts';
export * from './paymentGateway.ts';
export * from './posCatalog.ts';
export * from './scannerInput.ts';
export * from './zReport.ts';

export type Role = 'staff' | 'cashier' | 'manager' | 'owner';

export type AuthSecretType = 'pin' | 'password';

export type AuthLoginReason =
  | 'missing_user'
  | 'invalid_path'
  | 'invalid_secret'
  | 'locked'
  | 'locked_until'
  | 'ok';

export interface SecretHashParams {
  algorithm: 'bcrypt';
  cost: 12;
  migration: {
    migratedFrom: 'scrypt' | 'sha256-mock';
    migratedAt: string | null;
  } | null;
}

export interface AuthCacheRow {
  userId: string;
  secretType: AuthSecretType;
  secretHash: string;
  hashParams: SecretHashParams;
  failedAttempts: number;
  lockedUntil: string | null;
  updatedAt: string;
  role: Role;
}

export interface AuthLoginInput {
  userId: string;
  secretType: AuthSecretType;
  secret: string;
  now?: string;
}

export interface AuthLoginResult {
  ok: boolean;
  reason?: AuthLoginReason;
  lockedUntil?: string | null;
  actor?: {
    id: string;
    role: Role;
  };
}

export type PermissionAction =
  | 'dashboard.branches.read'
  | 'dashboard.masterData.write'
  | 'dashboard.promotions.write'
  | 'dashboard.system.write'
  | 'sale.line-discount'
  | 'sale.bill-discount'
  | 'sale.void'
  | 'sale.recall-others'
  | 'stock.adjust'
  | 'stock.in'
  | 'transfer.create'
  | 'transfer.receive'
  | 'transfer.cancel-post-sent'
  | 'shift.close-self'
  | 'shift.close-other'
  | 'reprint';

export interface PermissionOverride {
  action: PermissionAction;
  effect: 'allow' | 'deny';
  cap?: number;
}

export interface Branch {
  id: string;
  name: string;
  short: string;
  active?: boolean;
  deletedAt?: string | null;
}

export interface Device {
  id: string;
  tenantId: string;
  branchId: string;
  label: string;
  platform: 'windows' | 'macos' | 'linux';
  currentMasterDataVersion: number;
  lastSeenAt?: string | null;
}

export interface UserAccount {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  role: Role;
  branchIds: string[];
  active: boolean;
  pin?: string;
  permissionOverrides?: PermissionOverride[];
  deletedAt?: string | null;
}

export interface Category {
  id: string;
  name: string;
  active?: boolean;
  deletedAt?: string | null;
}

export interface ProductBarcode {
  id: string;
  productUnitId: string;
  barcode: string;
  type?: 'retail' | 'supplier' | 'legacy' | 'promo';
  active: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
  deletedAt?: string | null;
}

export interface ProductUnit {
  id: string;
  productId: string;
  code: string;
  name: string;
  barcodes?: ProductBarcode[];
  ratio: number;
  salePrice: number;
  active: boolean;
  deletedAt?: string | null;
}

export interface Product {
  id: string;
  name: string;
  categoryId: string;
  baseUnitId: string;
  active: boolean;
  minStock: number;
  imageUrl?: string;
  cost?: number;
  deletedAt?: string | null;
}

export interface ProductCatalogItem extends Product {
  units: ProductUnit[];
}

export interface BranchPrice {
  productId: string;
  unitId: string;
  branchId: string;
  price: number; // satang
  deletedAt?: string | null;
}

export interface Promotion {
  id: string;
  name: string;
  type: 'fixed_price' | 'percent_off' | 'bogo' | 'qty_tier' | 'bill_percent';
  value: number; // satang for fixed_price/qty_tier, percent or free quantity otherwise
  startDate: string;
  endDate: string;
  branchIds: string[];
  active: boolean;
  minBaht?: number; // satang
  minQty?: number;
  perBillLimit?: number;
  productIds?: string[];
  deletedAt?: string | null;
}

export interface SystemSettings {
  shopName: string;
  taxId: string;
  vatRate: number;
  vatInclusive: boolean;
  receiptHeader: string;
  receiptFooter: string;
  roundingStep: number;
  stockNegativeSaleBlock: boolean;
  cashierItemDiscountPct: number;
  cashierBillDiscountPct: number;
  managerItemDiscountPct: number;
  managerBillDiscountPct: number;
  ownerItemDiscountPct: number;
  ownerBillDiscountPct: number;
}

export interface DashboardSalesBranchRow {
  branchId: string;
  branchName: string;
  total: number;
  previousTotal: number;
  bills?: number;
  previousBills?: number;
}

export interface DashboardSalesSeriesRow {
  date: string;
  total: number;
}

export interface DashboardSalesProductRow {
  productId: string;
  productName: string;
  categoryId?: string;
  baseUnitName: string;
  /** Quantity as entered at checkout; only meaningful when one sale unit is used. */
  quantity: number;
  /** Quantity converted to the product's base stock unit. */
  baseQuantity: number;
  previousBaseQuantity: number;
  /** Line revenue before bill-level discount and rounding allocation. */
  amount: number;
  /** Revenue after allocating the receipt total back to its sale lines. */
  netAmount: number;
  previousNetAmount: number;
  previousQuantity: number;
  previousAmount: number;
  cogs: number;
  grossProfit: number;
  previousCogs: number;
  previousGrossProfit: number;
  /** Null when net revenue is zero. */
  margin: number | null;
  missingCostLineCount: number;
}

export interface DashboardSalesCategoryRow {
  categoryId: string;
  categoryName: string;
  amount: number;
}

export interface DashboardSalesPaymentRow {
  method: string;
  label: string;
  amount: number;
}

/** Promotion spend aggregated from immutable receipt snapshots. */
export interface DashboardPromotionSummaryRow {
  promotionId: string;
  promotionName: string;
  /** Number of confirmed receipts that used the promotion. */
  receiptCount: number;
  /** Total discount funded by the promotion, in satang. */
  discountAmount: number;
  /** Total free units granted by BOGO promotions. */
  freeQuantity: number;
}

export interface DashboardSalesReport {
  /** Net sales after promotions and bill-level discounts. */
  total: number;
  previousTotal: number;
  /** Sum before manual line, promotion, and bill discounts. */
  grossSales: number;
  /** Promotion-funded discount total for the selected period. */
  promotionDiscount: number;
  /** Manager/owner approved fixed per-item discount total. */
  manualLineDiscount?: number;
  /** Manual bill-level discount total for the selected period. */
  billDiscount: number;
  /** Net rounding adjustment applied after discounts for version 2 receipts. */
  roundingDelta: number;
  /** Legacy receipts whose gross/discount split is unavailable. */
  legacyAccountingReceiptCount: number;
  cogs: number;
  grossProfit: number;
  byBranch: DashboardSalesBranchRow[];
  daily: DashboardSalesSeriesRow[];
  monthly: DashboardSalesSeriesRow[];
  topByQty: DashboardSalesProductRow[];
  topByAmount: DashboardSalesProductRow[];
  products?: DashboardSalesProductRow[];
  categories: DashboardSalesCategoryRow[];
  payments: DashboardSalesPaymentRow[];
  promotions: DashboardPromotionSummaryRow[];
  discountVoid: {
    discounts: number;
    voids: number;
    refunds: number;
  };
}

export interface DashboardProductSalesDetailReport {
  summary: {
    baseUnitName: string;
    baseQuantity: number;
    grossAmount: number;
    netAmount: number;
    cogs: number;
    grossProfit: number;
    margin: number | null;
    missingCostLineCount: number;
  };
  daily: DashboardSalesSeriesRow[];
  byBranch: Array<{
    branchId: string;
    branchName: string;
    quantity: number;
    baseQuantity: number;
    amount: number;
    cogs: number;
    grossProfit: number;
  }>;
  byUnit: Array<{
    unitId: string;
    unitName: string;
    ratio: number;
    quantity: number;
    baseQuantity: number;
    grossAmount: number;
    netAmount: number;
    cogs: number;
    grossProfit: number;
    margin: number | null;
    missingCostLineCount: number;
  }>;
}

export interface ReceiptLineDto {
  id: string;
  productId: string;
  productUnitId: string;
  productName?: string;
  /** Immutable display name of the sold unit, e.g. ขวด/แพ็ก/ลัง. */
  unitName?: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  /** Manager/owner approved fixed discount for each sold unit. */
  manualDiscount?: ManualLineDiscountSnapshot;
}

export interface ReceiptPaymentDto {
  id: string;
  method: CanonicalPaymentMethod;
  amount: number;
  reference?: string | null;
  manualChannelId?: string;
  paymentLabel?: string;
}

export const RECEIPT_PROMOTION_TYPES = [
  'bill-amount',
  'bill-percent',
  'item-bogo',
  'item-fixed-price',
  'item-qty-tier',
  'item-percent',
] as const;

export type ReceiptPromotionType = (typeof RECEIPT_PROMOTION_TYPES)[number];

export const MANUAL_LINE_DISCOUNT_REASON_CODES = [
  'customer-care',
  'damaged-item',
  'special-price',
  'other',
] as const;

export type ManualLineDiscountReasonCode =
  (typeof MANUAL_LINE_DISCOUNT_REASON_CODES)[number];

/** Immutable manager/owner approval captured with a discounted sale line. */
export interface ManualLineDiscountSnapshot {
  /** Discount applied to one sold unit, in satang. */
  unitAmount: number;
  /** `unitAmount * quantity` at checkout, in satang. */
  totalAmount: number;
  reasonCode: ManualLineDiscountReasonCode;
  reasonNote?: string;
  approvedByUserId: string;
  approvedByRole: 'manager' | 'owner';
  appliedAt: string;
}

/** Immutable promotion result recorded when a sale is confirmed. */
export interface ReceiptPromotionDto {
  id: string;
  name: string;
  /** Discount applied to this receipt, in satang. */
  discountAmount: number;
  /** Stable checkout mechanic retained for accounting/audit. */
  promotionType?: ReceiptPromotionType;
  /** Number of free units granted by a BOGO promotion. */
  freeQuantity?: number;
  detail?: string;
}

export interface ReceiptPromotionParseResult {
  promotions: ReceiptPromotionDto[];
  valid: boolean;
}

const normalizeReceiptPromotion = (
  value: unknown,
): ReceiptPromotionDto | null => {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const id = typeof raw.id === 'string' ? raw.id.trim() : '';
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  if (
    !id ||
    !name ||
    typeof raw.discountAmount !== 'number' ||
    !Number.isInteger(raw.discountAmount) ||
    raw.discountAmount <= 0
  ) {
    return null;
  }
  if (
    raw.promotionType !== undefined &&
    !RECEIPT_PROMOTION_TYPES.includes(raw.promotionType as ReceiptPromotionType)
  ) {
    return null;
  }
  if (
    raw.freeQuantity !== undefined &&
    (!Number.isInteger(raw.freeQuantity) || Number(raw.freeQuantity) < 0)
  ) {
    return null;
  }
  if (raw.detail !== undefined && typeof raw.detail !== 'string') return null;

  return {
    id,
    name,
    discountAmount: raw.discountAmount,
    ...(raw.promotionType !== undefined
      ? { promotionType: raw.promotionType as ReceiptPromotionType }
      : {}),
    ...(raw.freeQuantity !== undefined
      ? { freeQuantity: Number(raw.freeQuantity) }
      : {}),
    ...(typeof raw.detail === 'string' && raw.detail.trim()
      ? { detail: raw.detail.trim() }
      : {}),
  };
};

/**
 * Parse the promotion snapshot through one canonical normalizer.
 * Readers may use the valid entries for legacy resilience; writers must reject
 * the input when `valid` is false so calculated discounts match persistence.
 */
export function parseReceiptPromotions(
  value: unknown,
): ReceiptPromotionParseResult {
  let decoded = value;
  if (typeof value === 'string') {
    try {
      decoded = JSON.parse(value) as unknown;
    } catch {
      return { promotions: [], valid: false };
    }
  }
  if (decoded === undefined || decoded === null) {
    return { promotions: [], valid: true };
  }
  if (!Array.isArray(decoded)) return { promotions: [], valid: false };

  const promotions: ReceiptPromotionDto[] = [];
  let valid = true;
  for (const raw of decoded) {
    const promotion = normalizeReceiptPromotion(raw);
    if (!promotion) {
      valid = false;
      continue;
    }
    promotions.push(promotion);
  }
  return { promotions, valid };
}

export interface ReceiptSummaryDto {
  saleId: string;
  receiptNo: string;
  receiptNumber: number;
  tenantId: string;
  branchId: string;
  deviceId: string;
  businessDate: string;
  soldAt: string;
  closedAt?: string | null;
  cashierId?: string | null;
  cashierName?: string | null;
  status: SaleLifecycleState;
  totalAmount: number;
  vatAmount: number;
  /** Manual bill-level discount, in satang. */
  billDiscount?: number;
  /** Final satang adjustment applied after discounts. */
  roundingDelta: number;
  copyCount: number;
  /** First time this receipt was issued as a full tax invoice. */
  taxInvoiceIssuedAt?: string | null;
  paymentMethods: CanonicalPaymentMethod[];
  appliedPromotions: ReceiptPromotionDto[];
}

export interface ReceiptDetailDto extends ReceiptSummaryDto {
  lines: ReceiptLineDto[];
  payments: ReceiptPaymentDto[];
  /** Buyer snapshot captured when this receipt was first issued as a tax invoice. */
  taxInvoiceCustomer?: TaxInvoiceCustomerInput | null;
}

export interface ReceiptSearchInput {
  branchId?: string;
  receiptNo?: string;
  saleId?: string;
  from?: string;
  to?: string;
  amount?: number;
  cashier?: string;
  limit?: number;
  offset?: number;
}

export interface ReceiptListResponse {
  receipts: ReceiptSummaryDto[];
  total: number;
  hasMore: boolean;
}

export type DashboardReceiptListResponse = ReceiptListResponse;

export interface DashboardReceiptDetailResponse {
  receipt: ReceiptDetailDto;
}

export interface DashboardTaxInvoiceIssueResponse {
  receiptNo: string;
  issuedAt: string;
}

export type TaxInvoiceBranchType = 'headquarters' | 'branch';

export interface TaxInvoiceCustomerInput {
  name: string;
  taxId: string;
  phone?: string;
  address: string;
  branchType: TaxInvoiceBranchType;
  branchNo?: string;
}

export interface TaxInvoiceCustomerDto extends TaxInvoiceCustomerInput {
  branchId: string;
  lastUsedAt: string;
}

export interface DashboardTaxInvoiceCustomerSearchResponse {
  customers: TaxInvoiceCustomerDto[];
}

export interface DashboardInventoryRow {
  branchId: string;
  branchName: string;
  productId: string;
  productName: string;
  categoryId: string | null;
  categoryName: string | null;
  onHand: number;
  cost: number;
  value: number;
  minStock: number;
  low: boolean;
}

export interface DashboardInventoryReport {
  rows: DashboardInventoryRow[];
  totalValue: number;
  lowCount: number;
}

export interface DashboardShiftPayoutRow {
  type: CashPayoutMethod;
  amount: number;
}

export interface DashboardShiftRow {
  shiftId: string;
  cashierName: string | null;
  branchId: string;
  branchName: string;
  businessDate: string;
  openedAt: string | null;
  closedAt: string | null;
  openingFloat: number;
  expectedCash: number;
  countedCash: number | null;
  discrepancy: number | null;
  status: 'open' | 'closed';
  settlement: 'open' | 'balanced' | 'discrepancy';
  payouts: DashboardShiftPayoutRow[];
}

export interface DashboardShiftReport {
  rows: DashboardShiftRow[];
  summary: {
    open: number;
    balanced: number;
    discrepancy: number;
  };
}

export type DashboardEvidenceOwnerType = 'payment' | 'cash_payout';

export type DashboardEvidenceItemStatus =
  'verified' | 'missing' | 'hash_mismatch' | 'failed';

export type DashboardEvidenceAlertType = 'hash_mismatch' | 'upload_failed';

export interface DashboardEvidenceVerificationDay {
  date: string;
  required: number;
  verified: number;
  missing: number;
  hashMismatch: number;
  failed: number;
}

export interface DashboardEvidenceVerificationItem {
  date: string;
  branchId: string;
  ownerType: DashboardEvidenceOwnerType;
  ownerId: string;
  saleId?: string;
  method?: string;
  amount: number;
  status: DashboardEvidenceItemStatus;
  verifiedEvidenceCount: number;
  latestAttemptAt?: string;
  lastErrorCode?: string;
  lastErrorMessage?: string;
  attemptCount?: number;
}

export interface DashboardEvidenceVerificationAlert {
  alertType: DashboardEvidenceAlertType;
  severity: 'warning' | 'critical';
  date: string;
  branchId: string;
  ownerType: DashboardEvidenceOwnerType;
  ownerId: string;
  message: string;
  attemptCount?: number;
  latestAttemptAt?: string;
}

export interface DashboardEvidenceVerificationReport {
  summary: {
    required: number;
    verified: number;
    missing: number;
    hashMismatch: number;
    failed: number;
  };
  days: DashboardEvidenceVerificationDay[];
  items: DashboardEvidenceVerificationItem[];
  alerts: DashboardEvidenceVerificationAlert[];
}

export interface DashboardZReport {
  businessDate: string;
  generatedAt: string;
  branches: DashboardSalesBranchRow[];
  sales: {
    gross: number;
    total: number;
    promotionDiscount: number;
    manualLineDiscount?: number;
    billDiscount: number;
    roundingDelta: number;
    legacyAccountingReceiptCount: number;
    cogs: number;
    grossProfit: number;
  };
  payments: DashboardSalesPaymentRow[];
  categories: DashboardSalesCategoryRow[];
  topByQty: DashboardSalesProductRow[];
  topByAmount: DashboardSalesProductRow[];
  promotions: DashboardPromotionSummaryRow[];
  cash: {
    expected: number;
    counted: number;
    discrepancy: number;
  };
  vat: {
    rate: number;
    taxableSales: number;
    vatAmount: number;
    netSales: number;
  };
  discountVoid: {
    discounts: number;
    voids: number;
    refunds: number;
  };
}

export interface CostSnapshot {
  unitCost: number; // satang
  currency: 'THB';
}

export type CanonicalPaymentMethod =
  'cash' | 'qr_gateway' | 'qr_personal' | 'reference_manual';
export const LEGACY_MANUAL_CHANNEL_ID = 'legacy-reference-manual';
export const LEGACY_MANUAL_PAYMENT_LABEL = 'โครงการรัฐ/reference/manual';
export type LegacyPaymentMethodAlias = 'qr' | 'card' | 'transfer' | 'qr_scb';
export type PaymentMethodCode =
  CanonicalPaymentMethod | LegacyPaymentMethodAlias;

export type SaleLifecycleState = 'payment_pending' | 'confirmed' | 'voided';
export type SaleLegacyStatus = 'normal' | 'refund';
export type SalePaymentState =
  'pending' | 'captured' | 'confirmed' | 'cancelled' | 'failed';

export type CashPayoutMethod = 'cash_drawer' | 'manager_transfer';
export type EvidenceOwnerType = 'payment' | 'cash_payout';
export type EvidenceUploadStatus =
  'local_only' | 'queued' | 'uploaded' | 'verified';

export interface PromptPayPersonalConfig {
  readonly promptpayId: string;
  readonly promptpayIdType: 'mobile' | 'national_id';
  readonly accountName: string;
  readonly staticQrPayload?: string;
}

export type PromptPayBranchMode = 'inherit' | 'override' | 'disabled';

export interface PromptPayAccount {
  readonly promptpayId: string;
  readonly promptpayIdType: 'mobile' | 'national_id';
  readonly accountName: string;
}

export type PromptPayBranchConfig =
  | { readonly mode: 'inherit' }
  | { readonly mode: 'disabled' }
  | ({ readonly mode: 'override' } & PromptPayAccount);

export interface ManualPaymentChannel {
  readonly id: string;
  readonly name: string;
  readonly active: boolean;
}

export interface BranchManualPaymentChannel {
  readonly branchId: string;
  readonly channelId: string;
  readonly displayLabel?: string;
  readonly active: boolean;
  readonly sortOrder: number;
}

export interface ManualChannelRef {
  readonly manualChannelId: string;
  readonly paymentLabel: string;
}

export interface ManualChannelPayment extends ManualChannelRef {
  readonly method: 'reference_manual';
  readonly amount: number;
}

export interface EffectiveManualPaymentChannel extends ManualPaymentChannel {
  readonly displayLabel: string;
  readonly sortOrder: number;
}

export interface EffectivePromptPayAccount extends PromptPayAccount {
  readonly mode: 'override';
  readonly source: 'store' | 'branch';
  readonly displayLabel: string;
  readonly sortOrder: number;
}

export interface BranchPaymentMethodSettingConfig {
  readonly qrPersonal?: PromptPayBranchConfig | PromptPayPersonalConfig;
}

export interface BranchPaymentMethodSetting {
  readonly method: CanonicalPaymentMethod;
  readonly displayLabel: string;
  readonly active: boolean;
  readonly requiresReference: boolean;
  readonly requiresEvidence: boolean;
  readonly sortOrder: number;
  readonly config?: BranchPaymentMethodSettingConfig;
}

export interface CustomerDisplayLine {
  readonly barcode: string;
  readonly name: string;
  readonly qty: number;
  readonly unit: string;
  readonly price: number; // satang
  readonly lineDiscount?: number; // satang
  readonly free?: boolean;
}

export interface CustomerDisplayPromo {
  readonly id: string;
  readonly name: string;
  readonly discountBaht: number; // satang
}

export interface CustomerDisplayState {
  readonly kind: 'greeting' | 'total' | 'cash' | 'qr' | 'thankYou';
  readonly message: string;
  readonly total?: number; // satang
  readonly rows?: ReadonlyArray<CustomerDisplayLine>;
  readonly count?: number;
  readonly subtotal?: number; // satang
  readonly billDiscount?: number; // satang
  readonly promoDiscount?: number; // satang
  readonly promos?: ReadonlyArray<CustomerDisplayPromo>;
  readonly countdown?: number;
  readonly provider?: string;
  readonly accountName?: string;
  readonly accountIdentifier?: string;
  readonly qrPayload?: string;
  readonly received?: number; // satang
  readonly change?: number; // satang
}

export interface CashierDisplayState {
  readonly kind: 'idle' | 'busy' | 'warning' | 'error';
  readonly message: string;
  readonly actor?: string;
}

export interface SaleLine {
  productId: string;
  productUnitId: string;
  quantity: number;
  unitPrice: number; // satang
  amount: number; // satang
  costSnapshot: CostSnapshot;
  costVersion: string;
}

export interface SalePayment {
  method: PaymentMethodCode;
  amount: number; // satang
  reference?: string;
  status?: SalePaymentState;
  idempotencyKey?: string;
  confirmedAt?: string;
  evidenceAttachmentId?: string;
  manualChannelId?: string;
  paymentLabel?: string;
}

export interface SaleRecord {
  id: string;
  tenantId: string;
  branchId: string;
  deviceId: string;
  businessDate: string;
  soldAt: string;
  status: SaleLegacyStatus;
  lifecycleState?: SaleLifecycleState;
  lines: SaleLine[];
  payments: SalePayment[];
  totalAmount: number; // satang
}

export interface StockMovement {
  id: string;
  branchId: string;
  productId: string;
  productUnitId: string;
  type: 'sale' | 'receive' | 'adjust' | 'transfer-in' | 'transfer-out';
  quantity: number;
  reason?: string;
  note?: string;
  occurredAt: string;
  costSnapshot: CostSnapshot;
  costVersion: string;
  sourceType?: string;
  sourceId?: string;
  sourceLineId?: string | null;
  baseQuantity?: number;
  unitRatioSnapshot?: number;
  saleUnitCostSnapshot?: number;
  unitCostInput?: number;
  valuationUnitCost?: number;
}

export interface TransferRecord {
  id: string;
  fromBranchId: string;
  toBranchId: string;
  status: 'draft' | 'sent' | 'received' | 'cancelled';
  createdAt: string;
  receivedAt?: string;
  lines: Array<{ productId: string; productUnitId: string; quantity: number }>;
}

export interface ShiftRecord {
  id: string;
  branchId: string;
  deviceId: string;
  openedAt: string;
  closedAt?: string | null;
  openedByUserId: string;
  closedByUserId?: string | null;
  businessDate?: string;
  openingCashAmount?: number; // satang
  expectedCashAmount?: number; // satang
  closingCashAmount?: number | null; // satang
  discrepancyAmount?: number | null; // satang
  discrepancyReason?: string | null;
  saleCount?: number;
  salesTotalAmount?: number; // satang
  paymentTotals?: Array<{
    method: PaymentMethodCode;
    amount: number; // satang
  }>;
}

interface CashPayoutRecordCommon {
  id: string;
  shiftId: string;
  branchId: string;
  deviceId: string;
  businessDate: string;
  actorUserId: string;
  method: CashPayoutMethod;
  amount: number; // satang
  vendorOrDescription: string;
  reason: string;
  createdAt: string;
  payoutNo?: number | null;
  expectedCashBefore?: number; // satang
  expectedCashAfter?: number; // satang
  expectedCashDelta?: number; // satang
}

export type CashPayoutRecord =
  | (CashPayoutRecordCommon & {
      readonly method: 'cash_drawer';
      /** For M2 cash drawer payouts: expectedCashAfter = expectedCashBefore + expectedCashDelta (typically negative). */
      readonly expectedCashDelta: number;
    })
  | (CashPayoutRecordCommon & {
      readonly method: 'manager_transfer';
      /** Manager transfer must not move drawer cash in M2 (expectedCashDelta = 0). */
      readonly expectedCashDelta: 0;
    });

export interface TransferSentPayload {
  transferId: string;
  createdByUserId?: string;
  fromBranchId: string;
  toBranchId: string;
  totalAmount?: number;
  sentAt: string;
  lines: Array<{
    productId: string;
    productUnitId: string;
    quantity: number;
  }>;
}

export interface TransferCancelledPayload {
  transferId: string;
  reason: string;
  cancelledAt: string;
}

export interface TransferReceivedPayload {
  transferId: string;
  receivedByUserId?: string;
  fromBranchId: string;
  toBranchId: string;
  receivedAt: string;
  lines: Array<{
    productId: string;
    productUnitId: string;
    quantity: number;
    unitCostInput?: number;
  }>;
}

export interface CashPayoutCreatedPayload {
  payoutId: string;
  shiftId: string;
  method: CashPayoutMethod;
  amount: number; // satang
  vendorOrDescription: string;
  reason: string;
  actorUserId: string;
  businessDate: string;
  payoutNo?: number | null;
  expectedCashBefore?: number; // satang
  expectedCashAfter?: number; // satang
}

export interface PaymentCapturedPayload {
  paymentId: string;
  saleId: string;
  method: PaymentMethodCode;
  amount: number; // satang
  status: SalePaymentState;
  manualChannelId?: string;
  paymentLabel?: string;
}

export interface PaymentEvidenceUploadedPayload {
  paymentId: string;
  saleId: string;
  evidenceAttachmentId: string;
  remoteKey: string;
  sha256: string;
  uploadedAt: string;
}

export interface SaleClosedLinePayload {
  readonly lineIndex: number;
  readonly saleLineId: string;
  readonly productId: string;
  readonly unitId: string;
  readonly quantity: number;
  /** Conversion rate from the sold unit to the product's base stock unit. */
  readonly unitRatioSnapshot?: number;
  /** Display name of the sold unit at checkout time. */
  readonly unitNameSnapshot?: string;
  /** Immutable product label for documents created outside a POS catalog UI. */
  readonly productNameSnapshot?: string;
  /** List unit price captured before manual line and bill-level discounts. */
  readonly unitPrice?: number;
  /** Manager/owner approved fixed per-unit discount snapshot. */
  readonly manualDiscount?: ManualLineDiscountSnapshot;
  readonly amount: number; // satang
  readonly costSnapshot: CostSnapshot;
  readonly cloudUnitCost?: number; // satang
}

export interface SaleClosedPayload {
  saleId: string;
  branchId: string;
  deviceId: string;
  documentSequence: number;
  /** Structured receipt identity; absent on legacy POS events. */
  receiptCode?: string;
  status: SaleLifecycleState;
  totalAmount: number; // satang
  closingAt: string;
  /** Promotions as applied at checkout; absent only for legacy POS events. */
  appliedPromotions?: readonly ReceiptPromotionDto[];
  /** Manual bill-level discount kept separate from sale line revenue. */
  billDiscount?: number;
  /** Final satang adjustment applied after discounts; required for version 2. */
  roundingDelta?: number;
  /** V2 separates promotions/bill discounts; V3 also snapshots manual lines. */
  promotionAccountingVersion?: 2 | 3;
  payments: Array<{
    paymentId: string;
    amount: number; // satang
    method: CanonicalPaymentMethod;
    manualChannelId?: string;
    paymentLabel?: string;
  }>;
  readonly lines?: readonly SaleClosedLinePayload[];
  /** Optional document metadata for a paid dashboard wholesale sale. */
  wholesale?: DashboardWholesaleSaleSnapshot;
}

/**
 * Stricter cloud-ingestion DTO for `sale.closed` events.
 * Requires non-empty line payloads with costSnapshot for normal sale close.
 * The existing SaleClosedPayload.lines remains optional for backward compatibility
 * with offline/M2 payloads; this type enforces the M3 cloud contract.
 */
export interface SaleClosedCloudPayload {
  saleId: string;
  branchId: string;
  deviceId: string;
  documentSequence: number;
  receiptCode?: string;
  status: SaleLifecycleState;
  totalAmount: number; // satang
  closingAt: string;
  appliedPromotions?: readonly ReceiptPromotionDto[];
  billDiscount?: number;
  roundingDelta?: number;
  promotionAccountingVersion?: 2 | 3;
  payments: Array<{
    paymentId: string;
    amount: number; // satang
    method: CanonicalPaymentMethod;
    manualChannelId?: string;
    paymentLabel?: string;
  }>;
  lines: readonly SaleClosedLinePayload[];
}

export interface ReceiptReprintedPayload {
  saleId: string;
  branchId: string;
  deviceId: string;
  businessDate: string;
  documentSequence: number;
  receiptCode?: string;
  copyCount: number;
  reprintedAt: string;
}

export interface ShiftClosedPayload {
  shiftId: string;
  branchId: string;
  deviceId: string;
  cashierUserId: string;
  businessDate: string;
  openedAt?: string;
  openingCashAmount: number;
  expectedCashAmount?: number;
  closingCashAmount: number | null;
  discrepancyAmount?: number;
}

export interface StockAdjustedPayload {
  movementIds: string[];
  branchId: string;
  occurredAt: string;
}

export type SyncEventPayloadByType = {
  'sale.closed': SaleClosedPayload;
  'receipt.reprinted': ReceiptReprintedPayload;
  'payment.captured': PaymentCapturedPayload;
  'stock.adjusted': StockAdjustedPayload;
  'shift.closed': ShiftClosedPayload;
  'transfer.sent': TransferSentPayload;
  'transfer.cancelled': TransferCancelledPayload;
  'transfer.received': TransferReceivedPayload;
  'cash_payout.created': CashPayoutCreatedPayload;
  'payment.evidence_uploaded': PaymentEvidenceUploadedPayload;
};

export type SyncEventEnvelopeByType<TType extends SyncEventType> = Omit<
  SyncEventEnvelope<SyncEventPayloadByType[TType]>,
  'eventType'
> & {
  eventType: TType;
};

export type AnySyncEventEnvelope = {
  [TType in SyncEventType]: SyncEventEnvelopeByType<TType>;
}[SyncEventType];

export interface EvidenceAttachment {
  id: string;
  ownerType: EvidenceOwnerType;
  ownerId: string;
  sha256: string;
  sizeBytes: number;
  mimeType: string;
  localReference: string;
  remoteKey?: string;
  uploadStatus: EvidenceUploadStatus;
  uploadAttemptCount?: number;
  lastUploadAttemptAt?: string | null;
  lastUploadErrorCode?: string | null;
  lastUploadErrorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * M3 ack status for sync events tracked on the local device.
 * Extends M2 (pending/inflight/done/failed/blocked) with cloud-side states.
 */
export type SyncQueueAckStatus =
  'pending' | 'accepted' | 'applied' | 'failed' | 'blocked';

/**
 * Response from the Worker sync ingestion endpoint.
 */
export interface SyncIngestionResponse {
  accepted: boolean;
  eventId: string;
  status: SyncQueueAckStatus;
  discrepancies?: ValuationDiscrepancyRecord[];
}

/**
 * Polling request to fetch which events have been applied on the cloud side.
 * tenantId and deviceId are derived from the JWT, not from this request.
 */
export interface SyncAppliedPollRequest {
  sinceEventSequence: number;
  limit?: number;
}

/**
 * Status of a single event in the poll response.
 * Only 'applied' (projection applied), 'pending' (in inbox, not yet processed),
 * or 'failed' (dead-lettered) are valid. 'accepted' and 'blocked' from
 * SyncQueueAckStatus are not used here because those states are transient
 * ingest-side and never returned by the poll endpoint.
 */
export type PollEventStatus = 'applied' | 'pending' | 'failed';

/**
 * Response from the applied-event poll endpoint.
 */
export interface SyncAppliedPollResponse {
  /**
   * Array of event status entries. Named 'applied' for historical/contract reasons;
   * each entry carries its own `status` field so 'pending' and 'failed' coexist.
   */
  applied: Array<{
    eventId: string;
    eventSequence: number;
    status: PollEventStatus;
    appliedAt?: string;
    /** Human-readable reason code for failure — only present when status is 'failed'. */
    reasonCode?: string;
  }>;
  /** Exclusive cursor — the last event_sequence in the returned batch. Send this
   * as since_event_sequence in the next poll to get the next batch without gaps. */
  nextSinceEventSequence: number | null;
  /** True when more events exist beyond this batch (backlog). Client should poll
   * again; pollAfterMs indicates how urgently. */
  hasMore: boolean;
  /** Device status. Always 'active' in the normal body because revoked devices
   * receive 403 before reaching this payload. 'locked' is reserved for future use. */
  deviceStatus: DeviceStatus;
  /** Latest master data version available on the server. */
  latestMasterDataVersion: number;
  /** Server timestamp in ISO 8601 format. */
  serverTime: string;
  /** Suggested poll interval in milliseconds. 0 = poll immediately (backlog remains). */
  pollAfterMs: number;
}

/**
 * Device status emitted in the sync/applied poll body.
 * Always 'active' in the response body (revoked = 403 before body is sent).
 * 'locked' is reserved for future device-locking support.
 */
export type DeviceStatus = 'active' | 'locked';

/**
 * Authorization response for evidence upload to R2.
 */
export interface EvidenceUploadAuthResponse {
  uploadUrl: string;
  remoteKey: string;
  expiresAt: string;
}

/**
 * Acknowledgment after evidence has been uploaded to R2.
 */
export interface EvidenceUploadAck {
  eventId: string;
  remoteKey: string;
  sha256: string;
  sizeBytes: number;
  uploadedAt: string;
}

/**
 * Notification sent to target device when a transfer is received.
 */
export interface IncomingTransferNotification {
  transferId: string;
  fromBranchId: string;
  fromBranchName: string;
  toBranchId: string;
  lines: Array<{
    productId: string;
    productUnitId: string;
    quantity: number;
    productName: string;
    unitName: string;
  }>;
  sentAt: string;
  receivedAt?: string;
}

/**
 * Device heartbeat sent to Worker to report liveness and sync state.
 *
 * @deprecated M3 decision: no separate heartbeat timer.
 * Device health is derived from authenticated sync/poll/master requests
 * (M3-OPS-02). This type is kept for backward compat but is NOT wired
 * to any route or consumer. Remove after M3-DASH-04 confirms no
 * downstream usage.
 */
export interface HeartbeatRequest {
  deviceId: string;
  tenantId: string;
  branchId: string;
  timestamp: string;
  masterDataVersion: number;
  lastSyncSequence: number;
  pendingEventCount: number;
  uptimeSeconds: number;
}

/**
 * Response to a device heartbeat.
 *
 * @deprecated See HeartbeatRequest — no heartbeat route in M3.
 */
export interface HeartbeatResponse {
  acknowledged: boolean;
  serverTime: string;
  masterDataNotification?: MasterDataNotification;
}

/**
 * Aggregate sync-health status for a single device, used by the dashboard.
 */
export type DeviceHealthStatusValue = 'online' | 'stale' | 'offline';

/**
 * Aggregate sync-health status for a single device, used by the dashboard.
 */
export interface DeviceHealthStatus {
  deviceId: string;
  branchId: string;
  label: string;
  credentialState: DeviceCredentialState;
  accessTokenExpiresAt: string | null;
  refreshTokenExpiresAt: string | null;
  revokedAt: string | null;
  lastSeenAt: string | null;
  currentMasterDataVersion: number;
  latestMasterDataVersion: number;
  pendingEventCount: number;
  lastSyncSequence: number;
  uptimeSeconds: number;
  /** @deprecated Use `status` instead — mapped from status per-device. Kept for backward compat during M3-DASH-04 transition. */
  stale: boolean;
  status: DeviceHealthStatusValue;
  appVersion: string | null;
  dlqCount: number;
  failedEventCount: number;
}

/**
 * Response from the device-health dashboard endpoint.
 * Returns per-device health data plus aggregated statistics.
 */
export interface DeviceHealthQueryResponse {
  devices: DeviceHealthStatus[];
  totalDevices: number;
  onlineCount: number;
  staleCount: number;
  offlineCount: number;
  /** Threshold in ms — devices with lastSeenAt within this window are 'online'. */
  onlineThresholdMs: number;
  /** Threshold in ms — devices with lastSeenAt beyond this window are 'offline'. */
  offlineThresholdMs: number;
}

/**
 * A sync-event gap row: an event on the local queue that has not been acknowledged by the cloud.
 */
export interface SyncGapRow {
  eventId: string;
  eventSequence: number;
  entityType: string;
  entityId: string;
  status: SyncQueueAckStatus;
  createdAt: string;
}

/**
 * A dead-letter queue entry for an event that has exhausted retries.
 */
export interface SyncDLQEntry {
  eventId: string;
  eventSequence: number;
  entityType: string;
  entityId: string;
  retryCount: number;
  resolutionStatus?: string;
  lastError?: string;
  lastAttemptedAt?: string;
}

/**
 * Full sync-health report for a device, used by dashboard sync-health views.
 */
export interface SyncHealthReport {
  deviceId: string;
  pendingCount: number;
  failedCount: number;
  blockedCount: number;
  gaps: SyncGapRow[];
  dlq: SyncDLQEntry[];
  lastAppliedSequence: number;
  lastAppliedAt?: string;
}

/**
 * Per-branch payment method settings, synced via master-data pull.
 */
export interface BranchPaymentSettingsRecord {
  branchId: string | null;
  methods: BranchPaymentMethodSetting[];
}

export interface PaymentMethodMasterRecord {
  readonly method: CanonicalPaymentMethod;
  readonly displayLabel: string;
  readonly active: boolean;
}

export interface EffectivePaymentSettings {
  readonly branchId: string;
  readonly cashMethod: BranchPaymentMethodSetting | null;
  readonly gatewayMethods: readonly BranchPaymentMethodSetting[];
  readonly promptPay: EffectivePromptPayAccount | null;
  readonly manualChannels: readonly EffectiveManualPaymentChannel[];
}

export interface PaymentSettingsSource {
  readonly synchronized: boolean;
  readonly paymentMethods?: readonly PaymentMethodMasterRecord[];
  readonly paymentRecords: readonly BranchPaymentSettingsRecord[];
  readonly channels: readonly ManualPaymentChannel[];
  readonly assignments: readonly BranchManualPaymentChannel[];
}

export type MasterDataEntity =
  | 'branches'
  | 'categories'
  | 'devices'
  | 'users'
  | 'products'
  | 'product_units'
  | 'product_barcodes'
  | 'branch_prices'
  | 'settings'
  | 'promotions'
  | 'payment_methods'
  | 'branch_payment_settings'
  | 'manual_payment_channels'
  | 'branch_manual_payment_channels';

export const MASTER_DATA_ENTITIES: MasterDataEntity[] = [
  'branches',
  'categories',
  'devices',
  'users',
  'products',
  'product_units',
  'product_barcodes',
  'branch_prices',
  'settings',
  'promotions',
  'payment_methods',
  'branch_payment_settings',
  'manual_payment_channels',
  'branch_manual_payment_channels',
];

export interface MasterDataChange<T = unknown> {
  op: 'upsert' | 'delete';
  id: string;
  version: number;
  updatedAt: string;
  data?: T;
  deletedAt?: string;
}

export interface MasterDataPullResponse<T = unknown> {
  entity: MasterDataEntity;
  schemaVersion: string;
  tenantId: string;
  branchId: string;
  cursor: string | null;
  nextCursor: string | null;
  hasMore: boolean;
  masterDataVersion: number;
  changes: MasterDataChange<T>[];
}

export interface MasterDataVersionEnvelope {
  tenantId: string;
  branchIds: string[];
  masterDataVersion: number;
  cursor: string;
  changedEntities: MasterDataEntity[];
  notifiedDeviceIds: string[];
}

/**
 * Response from the multi-entity master data pull endpoint (M3-WRK-07).
 * Returns changes across all master data entities since the requested version.
 */
export interface MasterDataPullAllResponse {
  /** Latest global master data version — cursor for next pull request. */
  masterDataVersion: number;
  /** Per-entity pull results with their changes, cursors, and pagination state. */
  entities: Partial<Record<MasterDataEntity, MasterDataEntityPullResult>>;
}

/**
 * Per-entity result within a MasterDataPullAllResponse.
 * `hasMore` tells the client whether this entity has more pages after this batch.
 * `cursor` is the exclusive version cursor — the last version in this batch.
 * Send it back as `cursor_<entity>=<value>` to get the next page for this entity.
 * When all entities have `hasMore: false`, the global `masterDataVersion` has
 * advanced and the client can start fresh with `since_version=<masterDataVersion>`.
 */
export interface MasterDataEntityPullResult {
  changes: MasterDataChange[];
  cursor: string;
  hasMore: boolean;
}

export interface MasterDataWriteRequest<T = unknown> {
  tenantId: string;
  branchIds: string[];
  actorUserId: string;
  deviceIds?: string[];
  data: T;
}

export interface DeviceSyncStatus {
  deviceId: string;
  branchId: string;
  currentMasterDataVersion: number;
  latestMasterDataVersion: number;
  lastSeenAt?: string | null;
  stale: boolean;
}

export interface MasterDataNotification {
  tenantId: string;
  branchIds: string[];
  cursor: string;
  masterDataVersion: number;
  changedEntities: MasterDataEntity[];
  deviceIds: string[];
  reason: 'dashboard-write' | 'manual-resync';
}

export interface SyncEventEnvelope<TPayload = unknown> {
  tenantId: string;
  branchId: string;
  deviceId: string;
  eventId: string;
  idempotencyKey: string;
  schemaVersion: string;
  occurredAt: string;
  eventType: SyncEventType;
  /** Monotonic per-device sequence for ordering. Required for M3 cloud ingestion. */
  eventSequence: number;
  payload: TPayload;
}

export interface ProductCostLedgerEntry {
  id: string;
  tenantId: string;
  productId: string;
  eventId: string;
  effectiveAt: string;
  unitCostInput: number; // satang
  quantity: number;
  runningQty: number;
  runningTotal: number; // satang (cumulative qty * unit_cost)
  movingAverageCost: number; // satang
  processedAt: string;
}

export interface ValuationDiscrepancyRecord {
  id: string; // deterministic: ${tenantId}:${eventId}:${saleLineIndex}
  tenantId: string;
  branchId: string;
  eventId: string;
  saleId?: string;
  saleLineIndex?: number;
  productId: string;
  localUnitCost: number;
  cloudUnitCost: number;
  difference: number;
  recordedAt: string;
}

export interface ArtifactBindingInput {
  tenantId: string;
  branchId?: string;
  deviceId?: string;
  userId?: string;
  businessDate?: string;
  paymentId?: string;
  channel?: string;
  version?: string;
  fileName?: string;
  extension?: string;
  timestamp: string;
  sha256?: string;
  jobId?: string;
}

export type BackupRetentionClass = 'daily' | 'monthly' | 'manual' | 'pilot';

export interface BackupObjectMetadata {
  tenantId: string;
  branchId: string;
  branchCode: string;
  deviceId: string;
  deviceCode: string;
  objectKey: string;
  createdAt: string;
  appVersion: string;
  dbSizeBytes: number;
  encryptedSizeBytes: number;
  sha256: string;
  retentionClass: BackupRetentionClass;
}

export interface BackupUploadResponse {
  verified: true;
  backup: BackupObjectMetadata;
}

export interface BackupListResponse {
  backups: BackupObjectMetadata[];
}

export interface UpdateArtifactMetadata {
  channel: 'stable' | 'beta';
  version: string;
  fileName: string;
  blockmapFileName?: string;
  checksumSha256: string;
  publishedAt: string;
  minimumSupportedVersion?: string;
  unsignedPublisherWarning: string;
}

export type ApiErrorCode =
  | 'bad_request'
  | 'validation_error'
  | 'duplicate_event'
  | 'reconciliation_candidate_fanout_exceeded'
  | 'reconciliation_export_range_too_large'
  | 'forbidden'
  | 'not_found'
  | 'unsupported_schema'
  | 'internal_error'
  | 'auth_error'
  | 'auth_required'
  | 'rate_limited'
  | 'device_revoked'
  | 'device_not_found'
  | 'invalid_credentials'
  | 'invalid_token'
  | 'token_expired'
  | 'enrollment_invalid'
  | 'enrollment_expired'
  | 'enrollment_already_used'
  | 'device_already_registered'
  | 'verification_failed';

export interface ApiErrorEnvelope {
  ok: false;
  error: {
    code: ApiErrorCode;
    message: string;
    details?: Record<string, unknown>;
  };
}

export interface ApiSuccessEnvelope<T> {
  ok: true;
  data: T;
}

export interface DashboardOverview {
  businessDate: string;
  kpis: {
    revenue: number;
    bills: number;
    averageBill: number;
    growth: number | null;
    cashShare: number | null;
    qrShare: number | null;
  };
  payments: Array<{
    method: string;
    label: string;
    amount: number;
    share: number;
  }>;
  trend: Array<{ date: string; amount: number }>;
  branches: Array<{
    branchId: string;
    branchName: string;
    revenue: number;
    bills: number;
    growth: number | null;
    connectivity: 'online' | 'syncing' | 'offline' | 'unknown';
  }>;
  topProducts: Array<{
    productId: string;
    productName: string;
    quantity: number;
    amount: number;
  }>;
  recentReceipts: Array<{
    saleId: string;
    receiptNo: string;
    soldAt: string;
    totalAmount: number;
    paymentMethods: string[];
  }>;
  alerts: Array<{
    id: string;
    severity: 'danger' | 'warn' | 'info';
    title: string;
    detail: string;
  }>;
}

export interface DashboardTransferLine {
  productId: string;
  unitId: string;
  quantity: number;
}

export interface DashboardWholesaleCustomerSnapshot {
  name?: string;
  taxId?: string;
  phone?: string;
  address?: string;
}

export interface DashboardWholesaleSaleSnapshot {
  customer?: DashboardWholesaleCustomerSnapshot;
  note?: string;
}

export interface DashboardWholesaleSaleLineInput {
  productId: string;
  unitId: string;
  quantity: number;
  unitPrice: number;
}

export interface DashboardWholesaleSaleCreateInput extends DashboardWholesaleSaleSnapshot {
  requestId: string;
  branchId: string;
  lines: DashboardWholesaleSaleLineInput[];
}

export interface DashboardWholesaleSaleLine extends DashboardWholesaleSaleLineInput {
  productName: string;
  unitName: string;
  amount: number;
}

export interface DashboardWholesaleSale {
  id: string;
  receiptNo: string;
  branchId: string;
  soldAt: string;
  totalAmount: number;
  paymentMethod: 'cash';
  customer?: DashboardWholesaleCustomerSnapshot;
  note?: string;
  lines: DashboardWholesaleSaleLine[];
}

export interface DashboardTransfer {
  id: string;
  createdByUserId?: string;
  createdByName?: string;
  receivedByUserId?: string;
  receivedByName?: string;
  fromBranchId: string;
  toBranchId: string;
  status: 'sent' | 'received' | 'cancelled';
  createdAt: string;
  receivedAt?: string;
  lines: DashboardTransferLine[];
}

export interface DashboardTransferCreateInput {
  requestId: string;
  fromBranchId: string;
  toBranchId: string;
  lines: DashboardTransferLine[];
}

export interface DashboardTransferReceiveInput {
  requestId: string;
}

export interface DashboardSecurityEventInput {
  event: 'permission_denied';
  action: string;
  path: string;
  branchId?: string;
}

// ─── M3-WRK-02: Batch sync event ingestion types ────────────────────────

/**
 * Batch request body for POST /sync/events.
 */
export interface BatchSyncEventsRequest {
  events: SyncEventEnvelope[];
}

/**
 * Per-event result in the batch response.
 * - accepted: inserted into sync_inbox and queued for consumer
 * - already_seen: duplicate event_id or idempotency_key (no double-enqueue)
 * - rejected: schema/scope/payload validation failure
 * - retryable_error: persisted but not enqueued; client must retry
 */
export interface SyncEventIngestResult {
  eventId: string;
  status: 'accepted' | 'already_seen' | 'rejected' | 'retryable_error';
  message?: string;
}

/**
 * Batch response for POST /sync/events.
 */
export interface BatchSyncEventsResponse {
  results: SyncEventIngestResult[];
  summary: {
    accepted: number;
    alreadySeen: number;
    rejected: number;
  };
}

/**
 * M3-WRK-01: Dashboard user login request.
 */
export interface AuthLoginRequest {
  phone: string;
  password: string;
  tenantId: string;
}

/**
 * M3-WRK-01: Dashboard user login response.
 */
export interface AuthLoginResponse {
  token: string;
  refreshToken: string;
  expiresAt: string;
  user: {
    id: string;
    name: string;
    phone: string;
    email?: string;
    role: Role;
    branchIds: string[];
    permissionOverrides?: PermissionOverride[];
  };
}

/**
 * M3-WRK-01: POS device login/PIN verification request.
 */
export interface DeviceLoginRequest {
  deviceId: string;
  userId: string;
  secretType: 'pin' | 'password';
  secret: string;
  tenantId: string;
  branchId: string;
}

/**
 * M3-WRK-01: POS device login response (cashier session token).
 */
export interface DeviceLoginResponse {
  token: string;
  expiresAt: string;
  user: {
    id: string;
    name: string;
    role: Role;
    branchIds: string[];
  };
}

export type DeviceCredentialState = 'active' | 'refresh_required' | 'revoked';

export interface DeviceManagementRecord {
  deviceId: string;
  branchId: string;
  label: string;
  credentialState: DeviceCredentialState;
  accessTokenExpiresAt: string | null;
  refreshTokenExpiresAt: string | null;
  revokedAt: string | null;
}

/**
 * M3-WRK-01: Create device enrollment code (owner via dashboard).
 */
export interface CreateEnrollmentRequest {
  tenantId: string;
  branchId: string;
  label: string;
  createdByUserId: string;
}

/**
 * M3-WRK-01: Enrollment code created response.
 */
export interface CreateEnrollmentResponse {
  code: string;
  expiresAt: string;
}

export interface CreateDeviceRecoveryCodeRequest {
  label?: string;
}

export interface CreateDeviceRecoveryCodeResponse extends CreateEnrollmentResponse {
  deviceId: string;
}

/**
 * M3-WRK-01: POS device registration with enrollment code.
 */
export interface DeviceRegisterRequest {
  code: string;
  fingerprint: string;
  label?: string;
  platform?: 'windows' | 'macos' | 'linux';
}

/**
 * M3-WRK-01: POS device registration response.
 */
export interface DeviceCredentialPair {
  deviceToken: string;
  expiresAt: string;
  deviceRefreshToken: string;
  deviceRefreshTokenExpiresAt: string;
}

export interface DeviceRegisterResponse extends DeviceCredentialPair {
  deviceId: string;
  deviceCode: string;
  tenantId: string;
  branchId: string;
  receiptRangeStart: number;
  receiptRangeEnd: number;
  receiptSeriesNo: number;
  receiptBranchPrefix: string;
}

export interface DeviceTokenRefreshRequest {
  deviceId: string;
  refreshToken: string;
}

export type DeviceTokenRefreshResponse = DeviceCredentialPair;

/**
 * M3-WRK-01: Token refresh request.
 */
export interface TokenRefreshRequest {
  refreshToken: string;
}

/**
 * M3-WRK-01: Token refresh response.
 */
export interface TokenRefreshResponse {
  token: string;
  refreshToken: string;
  expiresAt: string;
}

/**
 * M3-WRK-01: Device token claims embedded in JWT.
 */
export interface AuthTokenClaims {
  sub: string;
  tenantId: string;
  role: Role;
  type: 'device' | 'user';
  deviceId?: string;
  branchId?: string;
  permissionOverrides?: PermissionOverride[];
  iat: number;
  exp: number;
}

export type SyncEventType =
  | 'sale.closed'
  | 'receipt.reprinted'
  | 'payment.captured'
  | 'stock.adjusted'
  | 'shift.closed'
  | 'transfer.sent'
  | 'transfer.cancelled'
  | 'transfer.received'
  | 'cash_payout.created'
  | 'payment.evidence_uploaded';

export const DEFAULT_SYSTEM_SETTINGS: SystemSettings = {
  shopName: '',
  taxId: '',
  vatRate: 7,
  vatInclusive: true,
  receiptHeader: '',
  receiptFooter: '',
  roundingStep: 25, // satang — was 0.25 baht
  stockNegativeSaleBlock: true,
  cashierItemDiscountPct: 5,
  cashierBillDiscountPct: 5,
  managerItemDiscountPct: 10,
  managerBillDiscountPct: 10,
  ownerItemDiscountPct: 20,
  ownerBillDiscountPct: 20,
};

export function isMasterDataEntity(value: string): value is MasterDataEntity {
  return MASTER_DATA_ENTITIES.includes(value as MasterDataEntity);
}

const VALID_SALE_LIFECYCLE = [
  'payment_pending',
  'confirmed',
  'voided',
] as const;
const VALID_PAYMENT_METHODS = [
  'cash',
  'qr_gateway',
  'qr_scb',
  'qr_personal',
  'reference_manual',
] as const;
/**
 * Cloud-ingestion guard for `sale.closed` events.
 * Validates all required top-level fields of SaleClosedCloudPayload
 * and every line field including unitId, costSnapshot.
 * Enforces the M3 cloud contract before Worker projection.
 */
const isMoneyInt = (v: unknown): v is number =>
  typeof v === 'number' && Number.isInteger(v);

const isManualLineDiscountSnapshot = (
  value: unknown,
  quantity: number,
  unitPrice: unknown,
  amount: number,
): value is ManualLineDiscountSnapshot => {
  if (!value || typeof value !== 'object') return false;
  const discount = value as Record<string, unknown>;
  if (!isMoneyInt(discount.unitAmount) || discount.unitAmount <= 0)
    return false;
  if (!isMoneyInt(discount.totalAmount) || discount.totalAmount <= 0)
    return false;
  if (discount.totalAmount !== discount.unitAmount * quantity) return false;
  if (
    !MANUAL_LINE_DISCOUNT_REASON_CODES.includes(
      discount.reasonCode as ManualLineDiscountReasonCode,
    )
  ) {
    return false;
  }
  if (
    discount.reasonNote !== undefined &&
    typeof discount.reasonNote !== 'string'
  ) {
    return false;
  }
  if (
    typeof discount.approvedByUserId !== 'string' ||
    !discount.approvedByUserId.trim()
  ) {
    return false;
  }
  if (
    discount.approvedByRole !== 'manager' &&
    discount.approvedByRole !== 'owner'
  ) {
    return false;
  }
  if (
    typeof discount.appliedAt !== 'string' ||
    !Number.isFinite(Date.parse(discount.appliedAt))
  ) {
    return false;
  }
  if (isMoneyInt(unitPrice)) {
    if (discount.unitAmount > unitPrice) return false;
    if (amount !== unitPrice * quantity - discount.totalAmount) return false;
  }
  return true;
};

export function isSaleClosedCloudPayload(
  value: unknown,
): value is SaleClosedCloudPayload {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const c = value as Record<string, unknown>;

  // Top-level required fields
  if (typeof c.saleId !== 'string') return false;
  if (typeof c.branchId !== 'string') return false;
  if (typeof c.deviceId !== 'string') return false;
  if (typeof c.documentSequence !== 'number') return false;
  if (c.receiptCode !== undefined && typeof c.receiptCode !== 'string')
    return false;
  if (
    !VALID_SALE_LIFECYCLE.includes(
      c.status as (typeof VALID_SALE_LIFECYCLE)[number],
    )
  )
    return false;
  if (!isMoneyInt(c.totalAmount)) return false;
  if (typeof c.closingAt !== 'string') return false;
  if (
    c.billDiscount !== undefined &&
    (!isMoneyInt(c.billDiscount) || c.billDiscount < 0)
  ) {
    return false;
  }
  if (c.roundingDelta !== undefined && !isMoneyInt(c.roundingDelta)) {
    return false;
  }
  if (
    c.promotionAccountingVersion !== undefined &&
    c.promotionAccountingVersion !== 2 &&
    c.promotionAccountingVersion !== 3
  ) {
    return false;
  }
  const parsedPromotions = parseReceiptPromotions(c.appliedPromotions);
  if (!parsedPromotions.valid) return false;

  // payments: must be non-empty array with valid entries
  if (!Array.isArray(c.payments) || c.payments.length === 0) return false;
  const paymentsValid = (c.payments as Array<unknown>).every((p) => {
    if (typeof p !== 'object' || p === null) return false;
    const pm = p as Record<string, unknown>;
    const methodValid =
      typeof pm.paymentId === 'string' &&
      isMoneyInt(pm.amount) &&
      VALID_PAYMENT_METHODS.includes(
        pm.method as (typeof VALID_PAYMENT_METHODS)[number],
      );
    const hasManualChannelId =
      typeof pm.manualChannelId === 'string' &&
      pm.manualChannelId.trim().length > 0;
    const hasPaymentLabel =
      typeof pm.paymentLabel === 'string' && pm.paymentLabel.trim().length > 0;
    const snapshotValid =
      pm.method !== 'reference_manual' ||
      (!hasManualChannelId && !hasPaymentLabel) ||
      (hasManualChannelId && hasPaymentLabel);
    return methodValid && snapshotValid;
  });
  if (!paymentsValid) return false;

  // lines: must be non-empty array with full validation
  if (!Array.isArray(c.lines) || c.lines.length === 0) return false;
  const linesValid = (c.lines as Array<unknown>).every((line) => {
    if (typeof line !== 'object' || line === null) return false;
    const l = line as Record<string, unknown>;
    if (typeof l.lineIndex !== 'number') return false;
    if (typeof l.saleLineId !== 'string') return false;
    if (typeof l.productId !== 'string') return false;
    if (typeof l.unitId !== 'string') return false;
    if (typeof l.quantity !== 'number') return false;
    if (!isMoneyInt(l.amount)) return false;
    if (
      l.manualDiscount !== undefined &&
      (!isMoneyInt(l.unitPrice) ||
        !isManualLineDiscountSnapshot(
          l.manualDiscount,
          l.quantity,
          l.unitPrice,
          l.amount,
        ))
    ) {
      return false;
    }
    const cs = l.costSnapshot;
    if (typeof cs !== 'object' || cs === null) return false;
    const cost = cs as Record<string, unknown>;
    return isMoneyInt(cost.unitCost) && cost.currency === 'THB';
  });
  if (!linesValid) return false;
  const hasManualLineDiscount = (
    c.lines as Array<Record<string, unknown>>
  ).some((line) => line.manualDiscount !== undefined);
  if (hasManualLineDiscount && c.promotionAccountingVersion !== 3) {
    return false;
  }
  if (c.promotionAccountingVersion === 3 && !hasManualLineDiscount) {
    return false;
  }
  if (
    c.promotionAccountingVersion === 2 ||
    c.promotionAccountingVersion === 3
  ) {
    if (!isMoneyInt(c.roundingDelta)) return false;
    const grossLineTotal = (c.lines as Array<Record<string, unknown>>).reduce(
      (sum, line) => sum + Number(line.amount),
      0,
    );
    const promotionDiscount = parsedPromotions.promotions.reduce(
      (sum, promotion) => sum + promotion.discountAmount,
      0,
    );
    const billDiscount = Number(c.billDiscount ?? 0);
    if (promotionDiscount + billDiscount > grossLineTotal) return false;
    const expectedTotal =
      grossLineTotal - promotionDiscount - billDiscount + c.roundingDelta;
    if (expectedTotal < 0 || c.totalAmount !== expectedTotal) return false;
  }
  return true;
}

export function isSyncEventEnvelope(
  value: unknown,
): value is SyncEventEnvelope {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<SyncEventEnvelope>;
  return (
    typeof candidate.tenantId === 'string' &&
    typeof candidate.branchId === 'string' &&
    typeof candidate.deviceId === 'string' &&
    typeof candidate.eventId === 'string' &&
    typeof candidate.idempotencyKey === 'string' &&
    typeof candidate.schemaVersion === 'string' &&
    typeof candidate.occurredAt === 'string' &&
    typeof candidate.eventType === 'string' &&
    typeof candidate.eventSequence === 'number'
  );
}

export function findProductUnitByBarcode(
  products: ProductCatalogItem[],
  barcode: string,
  asOf: string | Date = new Date(),
): { product: ProductCatalogItem; unit: ProductUnit } | undefined {
  const timestamp =
    typeof asOf === 'string' ? Date.parse(asOf) : asOf.getTime();
  for (const product of products) {
    if (!product.active || product.deletedAt) {
      continue;
    }
    const unit = product.units.find(
      (entry) =>
        entry.active &&
        !entry.deletedAt &&
        entry.barcodes?.some((candidate) =>
          isBarcodeUsable(candidate, entry.id, barcode, timestamp),
        ),
    );
    if (unit) {
      return { product, unit };
    }
  }

  return undefined;
}

function isBarcodeUsable(
  candidate: ProductBarcode,
  productUnitId: string,
  barcode: string,
  asOf: number,
): boolean {
  if (
    candidate.productUnitId !== productUnitId ||
    candidate.barcode !== barcode ||
    !candidate.active ||
    candidate.deletedAt
  ) {
    return false;
  }
  if (candidate.startsAt) {
    const startsAt = Date.parse(candidate.startsAt);
    if (!Number.isFinite(startsAt) || startsAt > asOf) {
      return false;
    }
  }
  if (candidate.endsAt) {
    const endsAt = Date.parse(candidate.endsAt);
    if (!Number.isFinite(endsAt) || endsAt <= asOf) {
      return false;
    }
  }
  return true;
}

export function resolvePermissionOverride(
  overrides: PermissionOverride[] | undefined,
  action: PermissionAction,
): PermissionOverride | undefined {
  if (!overrides) {
    return undefined;
  }

  const matches = overrides.filter((override) => override.action === action);
  const deny = matches.find((override) => override.effect === 'deny');
  return deny ?? matches[matches.length - 1];
}

export function buildBackupObjectKey(input: ArtifactBindingInput): string {
  const branchCode = input.branchId ?? '';
  const deviceCode = input.deviceId ?? '';
  const timestamp =
    input.timestamp.length >= 16 && input.timestamp.includes('T')
      ? `${input.timestamp.slice(0, 4)}${input.timestamp.slice(5, 7)}${input.timestamp.slice(8, 10)}-${input.timestamp.slice(11, 13)}${input.timestamp.slice(14, 16)}`
      : input.timestamp;
  return `backups/${branchCode}/${deviceCode}/${timestamp}.sqlite.gz.enc`;
}

export function buildEvidenceObjectKey(input: ArtifactBindingInput): string {
  return [
    'evidence',
    input.tenantId,
    input.branchId,
    input.businessDate,
    input.paymentId,
    `${input.sha256}.${input.extension}`,
  ].join('/');
}

export function buildExportObjectKey(input: ArtifactBindingInput): string {
  return [
    'exports',
    input.tenantId,
    input.userId,
    input.timestamp.slice(0, 4),
    input.timestamp.slice(5, 7),
    input.timestamp.slice(8, 10),
    input.jobId,
    input.fileName,
  ].join('/');
}

export function buildUpdateObjectKey(input: ArtifactBindingInput): string {
  return [
    'updates',
    'win32',
    input.channel,
    input.version,
    input.fileName,
  ].join('/');
}

// ─── Audit types (M3-WRK-10: Master data CRUD audit log) ───────────────────

export type AuditAction =
  | 'master_data.create'
  | 'master_data.update'
  | 'master_data.delete'
  | 'sale.line_discount_applied'
  | 'stock.adjust'
  | 'stock.receive'
  | 'transfer.create'
  | 'transfer.receive'
  | 'permission_denied'
  | 'device.revoke'
  | 'device.recovery_code.create';

/** What an audit entry refers to: a master-data entity, stock, or device. */
export type AuditEntity =
  | MasterDataEntity
  | 'sale_item'
  | 'stock'
  | 'transfer'
  | 'security_event'
  | 'device';

export interface AuditLogEntry {
  tenantId: string;
  /** Branch the action affected; omitted (→ persisted as 'global') for tenant-wide changes. */
  branchId?: string;
  actorUserId: string;
  actorRole: Role;
  action: AuditAction;
  entity: AuditEntity;
  entityId: string;
  timestamp: string;
  /** Full serialized payload for create/update; null for delete to avoid leaking sensitive data. */
  payload?: Record<string, unknown> | null;
  /** Optional UI-ready Thai summary. Raw fields above remain authoritative. */
  display?: {
    title: string;
    detail?: string;
    productName?: string;
    actorName?: string;
  };
}

/**
 * Coarse UI-facing bucket for an audit entry. Many native `(action, entity)`
 * pairs collapse into one category (e.g. `stock.adjust` + `stock.receive` →
 * `stock-adjust`). This is a *derived* view, not a stored column — which is why
 * a category filter cannot be a plain equality check and must be expanded back
 * to its native predicate. Kept here so the worker query and the dashboard badge
 * derive from the same rules and cannot drift.
 */
export type AuditCategory =
  | 'device-management'
  | 'discount-applied'
  | 'stock-adjust'
  | 'price-edit'
  | 'product-create'
  | 'master-data';

/**
 * Ordered derivation rules: an entry belongs to the first rule whose `actions`
 * (if given) and `entities` (if given) both match. An entry matching none of the
 * special rules is `'master-data'` (the catch-all). The special rules are
 * mutually exclusive in practice (an entry cannot satisfy two at once), so a
 * category filter for a special category is exactly that rule's predicate, and
 * `master-data` is the negation of every rule.
 */
export const AUDIT_CATEGORY_RULES: ReadonlyArray<{
  category: Exclude<AuditCategory, 'master-data'>;
  actions?: readonly AuditAction[];
  entities?: readonly AuditEntity[];
}> = [
  {
    category: 'device-management',
    actions: ['device.revoke', 'device.recovery_code.create'],
  },
  {
    category: 'discount-applied',
    actions: ['sale.line_discount_applied'],
  },
  { category: 'stock-adjust', actions: ['stock.adjust', 'stock.receive'] },
  { category: 'price-edit', entities: ['branch_prices'] },
  {
    category: 'product-create',
    actions: ['master_data.create'],
    entities: ['products', 'product_units', 'product_barcodes'],
  },
];

export const AUDIT_CATEGORIES: readonly AuditCategory[] = [
  'device-management',
  'discount-applied',
  'stock-adjust',
  'price-edit',
  'product-create',
  'master-data',
];

export function isAuditCategory(value: unknown): value is AuditCategory {
  return (
    typeof value === 'string' &&
    (AUDIT_CATEGORIES as readonly string[]).includes(value)
  );
}

/** Derive the category of an audit entry (first matching rule; else catch-all). */
export function auditCategoryOf(entry: {
  action: AuditAction;
  entity: AuditEntity;
}): AuditCategory {
  for (const rule of AUDIT_CATEGORY_RULES) {
    const actionOk = !rule.actions || rule.actions.includes(entry.action);
    const entityOk = !rule.entities || rule.entities.includes(entry.entity);
    if (actionOk && entityOk) return rule.category;
  }
  return 'master-data';
}

// ─── Dashboard product list (server-side pagination + search) ──────────────

/** One row in the dashboard product list, with category/base-unit names resolved. */
export interface DashboardProductListItem {
  id: string;
  name: string;
  categoryId: string | null;
  categoryName: string | null;
  baseUnitId: string | null;
  baseUnitName: string | null;
  active: boolean;
}

/** Active/inactive filter for the dashboard product list. Defaults to 'all'. */
export type DashboardProductStatusFilter = 'active' | 'inactive' | 'all';

/** Filters (no pagination) shared by the list page query and its total count. */
export interface DashboardProductListFilter {
  tenantId: string;
  /** Case-insensitive literal substring match on product name or id, or an exact barcode. */
  search?: string;
  /** Exact product IDs. The HTTP route accepts at most 200 unique IDs per request. */
  ids?: string[];
  categoryId?: string;
  status?: DashboardProductStatusFilter;
}

/** One page of the dashboard product list: filters plus pagination window. */
export interface DashboardProductListQuery extends DashboardProductListFilter {
  limit?: number;
  offset?: number;
}

/** One page of the dashboard product list plus the full-result total. */
export interface DashboardProductListResponse {
  items: DashboardProductListItem[];
  total: number;
}
